import React from "react";
import { gql, useMutation } from "@apollo/client";
import { Form, Field } from "react-final-form";
import { Dialog } from "@syos-music/common";
import { SelectField } from "@syos-music/form";
import {
  Instrument,
  InstrumentType,
  MouthpieceTipOpening,
  MouthpieceTipOpeningSelectField,
  MaterialColorSelectField,
} from "@syos-music/instruments";
import { Product } from "@syos-music/products";

import { Grid, MenuItem, Tab, Tabs } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import SignatureMouthpieceSelectField from "components/signatureMouthpiece/SignatureMouthpieceSelectField";

import InventoryBox from "./InventoryBox";

const { OriginalsType, OriginalsTipOpeningSelectField } =
  Product.displayModes.originalsMouthpiece;

const MUTATION = gql`
  mutation ($box: String!, $row: String!, $column: String!, $sku: String!) {
    AddInventory(
      box: $box
      row: $row
      column: $column

      sku: $sku
    ) {
      inventory {
        id
        box

        ...InventoryBox_inventory
      }
    }
  }

  ${InventoryBox.fragments.inventory}
`;

const SIGNATURE_MOUTHPIECE_FRAGMENT = gql`
  fragment AddInventoryEntryDialog_signatureMouthpiece on SignatureMouthpiece {
    id

    mouthpieceRef {
      clarinetMouthpiece {
        id
        tipOpening
      }
    }
  }
`;

export default function AddInventoryEntryForm({ children, onSubmit }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [initialValues, setInitialValues] = React.useState();

  const open = React.useCallback(({ box, row, column }) => {
    setInitialValues({
      box: box.id,
      row,
      column,
      type: "originals",
      instrument: box.instrument,
      instrumentType: box.instrumentType,
    });
    setIsOpen(true);
  }, []);

  const close = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const [insert] = useMutation(MUTATION);

  const signatureSelectFieldRef = React.useRef();

  const handleSubmit = React.useCallback(
    (values) => {
      let sku;
      switch (values.type) {
        case "originals":
          sku = [
            "originals",
            values.instrument,
            values.instrumentType,
            values.originalsType,
            values.tipOpening,
            values.mouthpieceColor,
          ].join("|");
          break;

        case "signature": {
          if (values.instrument === "clarinet") {
            const { instrument, instrumentType, clarinetMouthpiece } =
              signatureSelectFieldRef.current.signatureMouthpiece.mouthpieceRef;

            values.tipOpening = MouthpieceTipOpening.fromMilimeters(
              instrument,
              instrumentType,
              clarinetMouthpiece.tipOpening / 100
            ).key;
          }

          sku = [
            "signature",
            values.instrument,
            values.instrumentType,
            values.signatureMouthpiece,
            values.tipOpening,
            values.mouthpieceColor,
          ].join("|");
          break;
        }

        default:
          return;
      }

      return insert({
        variables: {
          box: values.box,
          row: values.row,
          column: values.column,

          sku,
        },
      })
        .then(close)
        .catch(() => true);
    },

    [insert, close]
  );

  return (
    <>
      {children({ open })}

      <Dialog open={isOpen} onClose={close}>
        <Form onSubmit={handleSubmit} initialValues={initialValues}>
          {({ handleSubmit, submitting, values }) => {
            const { instrument, instrumentType } = values;

            return (
              <form onSubmit={handleSubmit} noValidate>
                <Dialog.Title color="primary">
                  Ajouter un bec de&nbsp;
                  <Instrument instrument={instrument} />
                  &nbsp;
                  <InstrumentType
                    instrument={instrument}
                    instrumentType={instrumentType}
                  />
                </Dialog.Title>

                <Field name="type" type="text">
                  {({ input }) => (
                    <Tabs
                      onChange={(e, value) => input.onChange(value)}
                      value={input.value}
                      variant="fullWidth"
                    >
                      <Tab value="originals" label="Originals" />
                      <Tab value="signature" label="Signature" />
                    </Tabs>
                  )}
                </Field>

                {values?.type === "originals" && (
                  <Dialog.Content>
                    <Grid container alignItems="flex-start">
                      <Grid item xs={12} md={4}>
                        <SelectField
                          name="originalsType"
                          required
                          label="Originals"
                        >
                          {OriginalsType.originalsTypes.map((originalsType) => (
                            <MenuItem key={originalsType} value={originalsType}>
                              <OriginalsType originalsType={originalsType} />
                            </MenuItem>
                          ))}
                        </SelectField>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <OriginalsTipOpeningSelectField
                          name="tipOpening"
                          required
                          instrument={instrument}
                          instrumentType={instrumentType}
                          originalsType={values.originalsType}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <MaterialColorSelectField
                          name="mouthpieceColor"
                          required
                          allowPrivate
                        />
                      </Grid>
                    </Grid>
                  </Dialog.Content>
                )}

                {values?.type === "signature" && (
                  <Dialog.Content>
                    <Grid container alignItems="flex-start">
                      <Grid
                        item
                        xs={12}
                        md={instrument === "saxophone" ? 4 : 6}
                      >
                        <SignatureMouthpieceSelectField
                          ref={signatureSelectFieldRef}
                          fragment={SIGNATURE_MOUTHPIECE_FRAGMENT}
                          filters={{
                            instrument: [instrument],
                            instrumentType: [instrumentType],
                          }}
                          name="signatureMouthpiece"
                          required
                        />
                      </Grid>

                      {instrument === "saxophone" && (
                        <Grid item xs={12} md={4}>
                          <MouthpieceTipOpeningSelectField
                            name="tipOpening"
                            instrument={instrument}
                            instrumentType={instrumentType}
                          />
                        </Grid>
                      )}

                      <Grid
                        item
                        xs={12}
                        md={instrument === "saxophone" ? 4 : 6}
                      >
                        <MaterialColorSelectField
                          name="mouthpieceColor"
                          required
                          allowPrivate
                        />
                      </Grid>
                    </Grid>
                  </Dialog.Content>
                )}

                <Dialog.Actions>
                  <LoadingButton type="submit" loading={submitting}>
                    Ajouter
                  </LoadingButton>
                </Dialog.Actions>
              </form>
            );
          }}
        </Form>
      </Dialog>
    </>
  );
}
